<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import CustomButton from "@/components/Button/Button.vue";
import CrewCollapsible from "@/components/CrewCollapsible/CrewCollapsible.vue";
import { useRoute, useRouter } from 'vue-router';
import { addCrewMember, getCrewListCrew } from "@/api";
import { CrewMember } from "@/models/CrewMember";
import { VDateInput } from "vuetify/labs/VDateInput";

const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const crewList = ref<CrewMember[]>([]);
const status = ref<"loading" | "success" | "error">("loading");

onMounted(() => {
    getCrewListCrew(route.params.id as string)
        .then(data => {
            crewList.value = data.data;
            status.value = "success";
        })
        .catch(() => {
            status.value = "error";
        });
});

const uploaded = ref([]);
const toggleValue = ref(false);
function navigateToCrewList() {
    router.push(`/crew-lists`);
}

const showAddNewCrewList = ref(false);

const newCrewMember = ref<Partial<CrewMember & { lastName: string }>>({
    name: '',
    lastName: '',
    email: '',
    nationality: '',
    phone: '',
    passportNumber: '',
    passportExpiry: new Date(),
    dateOfBirth: new Date(),
    sailingLicense: '',
});

function onSubmit() {
    addCrewMember(route.params.id as string, { ...newCrewMember.value, name: newCrewMember.value.name + ' ' + newCrewMember.value.lastName })
        .then(() => router.go(0))
}
</script>

<template>
    <div class="container flex flex-col gap-8 py-12" v-if="status === 'success'">
        <div class="flex justify-between align-center">
            <div class="flex gap-1 responsive-title-3 font-semibold header" @click="navigateToCrewList()">
                <img src="@/assets/icons/svg/chevron.svg" alt="Crew">
                <div>{{ t('crewList2.navigateBack') }}</div>
            </div>
            <div class="px-5" v-if="crewList && crewList.length !== 0">
                <CustomButton type="black" class="self-end  responsive-body-medium w-fit h-8" :rounded="true"
                    @click="showAddNewCrewList = true">
                    {{ t('crewList2.addNewMember') }}
                </CustomButton>
            </div>
        </div>
        <div class="flex justify-center items-center h-full gap-5 min-h-[400px] alignBoxClass"
            v-if="crewList && crewList.length === 0">
            <div class="flex flex-col bg-background-light boxClass justify-between rounded-8xl p-7.5">
                <div>
                    <img src="@/assets/icons/svg/Crew_40x40.svg" alt="Crew">
                </div>
                <div class=" responsive-body-large font-semibold">{{ t('crewList2.noCrewMember') }}</div>
            </div>
            <div @click="showAddNewCrewList = true"
                class="flex flex-col bg-primary-blue text-white justify-center items-center rounded-8xl p-12 gap-1 cursor-pointer boxClass desktop"
                type="primary">
                <img src="@/assets/icons/svg/Add_24x24_white.svg" alt="Plus" width="44" />
                <div class=" responsive-body-large font-semibold text-center">{{ t('crewList2.addNewMember') }}</div>
            </div>
        </div>
        <div class="flex flex-col gap-7" v-else-if="crewList && crewList.length !== 0">
            <div class="flex flex-col gap-4 px-12 rowScroll">
                <div v-for="(crew) in crewList" :key="crew.uuid" class="">
                    <CrewCollapsible :member="crew" />
                </div>
            </div>
        </div>
        <div class="px-7.5 flex justify-between align-center phone">
            <CustomButton type="black" class="self-start  responsive-body-medium w-25 h-13" :rounded="true"
                @click="showAddNewCrewList = true">
                {{ t('crewList2.buttons.add') }}
            </CustomButton>
        </div>
    </div>

    <CustomModal :show="showAddNewCrewList" classes="min-w-[720px]">
        <VForm class="flex flex-col p-6" @submit.prevent="onSubmit">
            <div class="cursor-pointer self-end" @click="showAddNewCrewList = false">
                <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18" />
            </div>
            <div class="flex flex-col gap-2 px-9 py-7.5 min-w-[720px]">
                <div class="flex flex-col gap-6">
                    <div class=" responsive-body-large font-semibold">{{ t('crewList2.addMemberTitle') }}</div>
                </div>
                <div class="bg-notifications-pending px-5 py-2.5 rounded-8xl">
                    <span class=" responsive-body-small font-semibold">{{ t('crewList2.infoNotice') }}</span>
                </div>
                <div class="flex flex-col">
                    <div class="flex gap-5">
                        <VTextField :label="t('crewList2.form.firstName')" v-model="newCrewMember.name"
                            variant="underlined"></VTextField>
                        <VTextField :label="t('crewList2.form.lastName')" v-model="newCrewMember.lastName"
                            variant="underlined"></VTextField>
                    </div>
                    <div class="flex gap-5">
                        <VTextField :label="t('crewList2.form.nationality')" v-model="newCrewMember.nationality"
                            variant="underlined"></VTextField>
                        <VDateInput v-model="newCrewMember.dateOfBirth" :label="t('crewList2.form.dateOfBirth')"
                            variant="underlined" />
                    </div>
                    <div class="flex gap-5">
                        <VTextField :label="t('crewList2.form.passportNumber')" v-model="newCrewMember.passportNumber"
                            variant="underlined"></VTextField>
                        <VDateInput v-model="newCrewMember.passportExpiry" :label="t('crewList2.form.passportExpiry')"
                            variant="underlined" />
                    </div>
                    <div class="flex gap-5 align-center py-3.5 rounded-8xl photoPass">
                        <div>
                            <div class="text-sm">{{ t('crewList2.form.passportPhoto') }}</div>
                            <span class=" responsive-body-small semiboldText">{{ t('crewList2.form.uploadNotice') }}</span>
                        </div>
                        <div>
                            <CustomButton type="outline-black" size="outlinesmall"
                                class="w-fit h-7 hover:bg-gray-200" :rounded="true">
                                {{ t('crewList2.buttons.browseFile') }}
                            </CustomButton>
                            <span>{{ t('crewList2.fileStatus.noFileChosen') }}</span>
                        </div>
                    </div>
                    <div class="customBoxClass">
                        <div class="flex gap-5 align-center py-3.5 rounded-8xl licensesClass">
                            <div>
                                <div class="text-sm">{{ t('crewList2.form.sailingLicense') }}</div>
                                <span class=" responsive-body-small semiboldText">{{ t('crewList2.form.uploadNotice') }}</span>
                            </div>
                            <div>
                                <CustomButton type="outline-black" size="outlinesmall"
                                    class="w-fit h-7 hover:bg-gray-200" :rounded="true">
                                    {{ t('crewList2.buttons.browseFile') }}
                                </CustomButton>
                                <span>{{ t('crewList2.fileStatus.noFileChosen') }}</span>
                            </div>
                        </div>
                        <div v-if="uploaded.length > 0">
                            <div v-for="file in uploaded" :key="file.id"
                                class="flex justify-between align-center py-3.5 px-5 customBorder">
                                <div class="flex gap-3">
                                    <span class=" responsive-body-small ">{{ file.name }}</span>
                                    <span class="responsive-body-xsmall semiboldText">{{ file.size }}</span>
                                </div>
                                <div class="flex gap-3">
                                    <div v-if="file.status === 'uploaded'">
                                        <img src="@/assets/icons/svg/Check cycle_16x16.svg" alt="Uploaded" />
                                    </div>
                                    <div v-else-if="file.status === 'failed'">
                                        <img src="@/assets/icons/svg/exclamation.svg" alt="Failed" />
                                    </div>
                                    <div v-else-if="file.status === 'progress'">
                                        <div class="responsive-body-xsmall semiboldText">{{ file.progress }}%</div>
                                    </div>
                                    <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex justify-between align-center">
                    <CustomButton type="black" class="self-start  responsive-body-medium w-25 h-13" :rounded="true"
                        @click="onSubmit">
                        {{ t('crewList2.buttons.add') }}
                    </CustomButton>
                </div>
            </div>
        </VForm>
    </CustomModal>
</template>

<style scoped>
.semiboldText {
    color: #8C8B94;
}

.boxClass {
    min-width: 200px;
    max-width: 220px;
    min-height: 240px;
    max-height: 260px;
}

.rowScroll {
    overflow-x: scroll;
}

@media (min-width: 768px) {
    .phone {
        display: none;
    }
}

@media (max-width: 768px) {
    .desktop {
        display: none;
    }

    .boxClass {
        min-width: 150px;
        max-width: 180px;
        min-height: 180px;
        max-height: 200px;
    }

    .rowScroll {
        overflow: unset !important;
    }
}
</style>
